import { useEffect, useState } from "react";
import type { FollowerState } from "@cuekards/core/speech";
import { IDLE_FOLLOWER } from "@cuekards/core/speech";

/**
 * The follower's state, pulled on mount and then followed live.
 *
 * The same shape every other view in this package uses to read host state — ask
 * for what the host already holds, then subscribe — extracted because two views
 * need it and neither is the other's parent. The transport draws the rehearse
 * link from it (scroll-and-bar ticket 01), and the prompter pane needs
 * `rehearsing` to know whether anything is driving the anchor (ticket 03).
 *
 * Two subscribers rather than one lifted into the shell on purpose: the shell has
 * no use for this, and threading it through would make `AppShell` re-render on
 * every backlog update — which arrives while a take is rolling, over a tree that
 * ticket 21 of the web effort spent real work on not re-rendering.
 *
 * The initial value is `IDLE_FOLLOWER` rather than a null, so the first paint
 * draws a disabled control with an explanation rather than a hole.
 */
export function useFollowerState(): FollowerState {
  const [state, setState] = useState<FollowerState>(IDLE_FOLLOWER);

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getFollowerState().then((s) => {
      if (!cancelled) setState(s);
    });
    const off = window.cuekards.onFollowerStateChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return state;
}
